import React, { ReactNode, useEffect, useRef } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { ComparisonValue, SecuritiesChartType } from "@components";
import { NoDataStub } from "@rentablo/components";
import { WithLoadingSpinner } from "@rentablo/components";
import { ChartSeriesData, ChartWrapper } from "../common";
import { useChartOptions } from "./useChartOptions";
import { Marker } from "./types";

type Props = {
  chartData: ChartSeriesData[];
  isFetching?: boolean;
  noDataMessage?: ReactNode;
  allowDrawing: boolean;
  chartType: SecuritiesChartType;
  currency?: string;
  showTooltipMobile?: boolean;
  comparedTicker?: ComparisonValue;
  mainValueLabel?: string;
  markers?: Marker[];
  tooltipSubtitleMap?: Record<string, string | undefined>;
  showNavigator?: boolean;
  chartHeight?: number;
  onChartClick?: (x?: number) => void;
};

// @ts-ignore
Highcharts.seriesTypes.area.prototype.getPointSpline =
  // @ts-ignore
  Highcharts.seriesTypes.spline.prototype.getPointSpline;

export const DrawLineChart = ({
  chartData,
  isFetching,
  noDataMessage,
  allowDrawing,
  chartType,
  currency,
  markers,
  mainValueLabel,
  comparedTicker,
  onChartClick,
  tooltipSubtitleMap,
  showTooltipMobile = true,
  showNavigator = true,
  chartHeight,
}: Props) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const isLoading = Boolean(isFetching);

  const chartOptions = useChartOptions(
    chartData,
    allowDrawing,
    chartType,
    showTooltipMobile,
    chartComponentRef,
    markers,
    mainValueLabel,
    currency,
    comparedTicker,
    onChartClick,
    tooltipSubtitleMap,
    showNavigator,
    chartHeight
  );

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;

    if (!chart || !showNavigator || !chartData.length) return;

    const timestamps = chartData.flatMap((series) =>
      series.data.map((point) => point[0])
    );

    if (!timestamps.length) return;

    const { minDate, maxDate } = timestamps.reduce(
      (acc, timestamp) => {
        if (timestamp < acc.minDate) acc.minDate = timestamp;
        if (timestamp > acc.maxDate) acc.maxDate = timestamp;
        return acc;
      },
      { minDate: Infinity, maxDate: -Infinity }
    );

    chart.xAxis[0]?.setExtremes(minDate, maxDate, true, false);
  }, [chartData, showNavigator]);

  useEffect(() => {
    chartComponentRef.current?.chart?.reflow();
  }, [chartOptions]);

  return (
    <Box pb={1}>
      <WithLoadingSpinner isLoading={isLoading} mb={isLoading ? 6 : 3}>
        {!chartData.length && !isFetching ? (
          <NoDataStub
            height={
              chartHeight
                ? `${chartHeight}px`
                : showNavigator
                  ? "440px"
                  : "300px"
            }
            secondLine={noDataMessage}
          />
        ) : (
          <ChartWrapper>
            <HighchartsReact
              // A new chart when the navigator comes or goes (e.g. turning a
              // phone): updating it in place keeps the navigator's margin and
              // its zoom.
              key={showNavigator ? "with-navigator" : "without-navigator"}
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartComponentRef}
            />
          </ChartWrapper>
        )}
      </WithLoadingSpinner>
    </Box>
  );
};
